import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Chip, Stack, Typography, Tooltip } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import { formatDurationDHMS } from '../../../utils/timeFormat';
import { getAddressText, renderAddressMapLink, resolveCoordinatePair } from './reportMapLinks';

const GeneratorFleetCompact = ({ reportData }) => {
    const vehicles = Array.isArray(reportData?.vehicles) ? reportData.vehicles : [];
    const [expandedIndex, setExpandedIndex] = useState(null);

    const totals = useMemo(() => {
        const totalOn = vehicles.reduce((sum, v) => sum + Number(v?.generatorOnDurationMin || 0), 0);
        const totalOff = vehicles.reduce((sum, v) => sum + Number(v?.generatorOffDurationMin || 0), 0);
        return { totalOn, totalOff };
    }, [vehicles]);

    if (!reportData) return null;

    return (
        <Stack spacing={1.2}>
            <Box
                sx={{
                    padding: 1.2,
                    borderRadius: 1.5,
                    border: '1px solid #d9e2ec',
                    background: '#f8fafc',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                }}
            >
                <Box>
                    <Typography variant="caption" sx={{ color: '#627d98' }}>Vehicles</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{vehicles.length}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#627d98' }}>Gen ON</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatDurationDHMS(totals.totalOn)}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: '#627d98' }}>Gen OFF</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatDurationDHMS(totals.totalOff)}</Typography>
                </Box>
            </Box>

            <Stack spacing={1}>
                {vehicles.length === 0 && (
                    <Typography variant="body2" sx={{ color: '#627d98' }}>
                        No generator fleet data available.
                    </Typography>
                )}
                {vehicles.map((vehicle, index) => {
                    const isExpanded = expandedIndex === index;
                    const onDuration = formatDurationDHMS(vehicle?.generatorOnDurationMin || 0);
                    const offDuration = formatDurationDHMS(vehicle?.generatorOffDurationMin || 0);
                    const routeLength = Number(vehicle?.routeLength || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                    return (
                        <Box
                            key={`${vehicle?.imei || 'vehicle'}-${index}`}
                            onClick={() => setExpandedIndex(isExpanded ? null : index)}
                            sx={{
                                borderRadius: 1.5,
                                border: `1px solid ${isExpanded ? '#0ea5a0' : '#d9e2ec'}`,
                                padding: 1.2,
                                cursor: 'pointer',
                                background: isExpanded ? '#dff5f3' : '#fff',
                                boxShadow: isExpanded ? '0 10px 20px rgba(15, 23, 42, 0.08)' : 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: '#0ea5a0',
                                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                                },
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.8}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#102a43' }}>
                                            {vehicle?.vehicleName || 'Vehicle'}
                                        </Typography>
                                        {isExpanded ? (
                                            <ExpandLessRoundedIcon fontSize="small" sx={{ color: '#0ea5a0' }} />
                                        ) : (
                                            <ExpandMoreRoundedIcon fontSize="small" sx={{ color: '#7b8794' }} />
                                        )}
                                    </Stack>
                                    <Stack direction="row" spacing={0.6} alignItems="baseline">
                                        <Typography variant="caption" sx={{ color: '#627d98', fontWeight: 600 }}>
                                            Route
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ color: '#102a43', fontWeight: 700 }}>
                                            {routeLength} km
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Stack spacing={0.5} alignItems="flex-end">
                                    <Chip
                                        size="small"
                                        label={`ON ${onDuration}`}
                                        sx={{
                                            fontWeight: 700,
                                            color: '#ffffff',
                                            backgroundColor: '#0f766e',
                                            '& .MuiChip-label': { px: 0.8 },
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ color: '#7b8794', fontWeight: 600 }}>
                                        OFF {offDuration}
                                    </Typography>
                                </Stack>
                            </Stack>
                            {isExpanded && (
                                <Stack spacing={0.8} sx={{ marginTop: 1 }}>
                                    {(vehicle?.runningPeriods || []).length === 0 && (
                                        <Typography variant="caption" sx={{ color: '#627d98' }}>
                                            No running periods found.
                                        </Typography>
                                    )}
                                    {(vehicle?.runningPeriods || []).map((period, pIndex) => {
                                        const startTime = period?.startTime ? moment(period.startTime).format('DD MMM, h:mm A') : '--';
                                        const endTime = period?.endTime ? moment(period.endTime).format('DD MMM, h:mm A') : '--';
                                        const duration = formatDurationDHMS(period?.durationMinutes || 0);
                                        const side = String(period?.generatorSide || '').toUpperCase();
                                        const addressSource = period?.startAddress || period?.endAddress;
                                        const addressType = period?.startAddress ? 'start' : 'end';
                                        const address = getAddressText(addressSource, '');
                                        const hasMapTarget = Boolean(address || resolveCoordinatePair({
                                            address: addressSource,
                                            source: period,
                                            type: addressType,
                                        }));
                                        return (
                                            <Box
                                                key={`${vehicle?.imei}-${pIndex}`}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr auto',
                                                    gap: 1,
                                                    padding: 0.8,
                                                    borderRadius: 1,
                                                    border: '1px solid #e2e8f0',
                                                    background: '#fff',
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" sx={{ color: '#627d98', fontWeight: 600 }}>
                                                        {startTime} → {endTime}
                                                    </Typography>
                                                    {hasMapTarget && (
                                                        <Tooltip title={address || 'Click Here'}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: '#102a43',
                                                                    display: 'block',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                {renderAddressMapLink({
                                                                    address: addressSource,
                                                                    source: period,
                                                                    type: addressType,
                                                                })}
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" sx={{ color: '#627d98', fontWeight: 700 }}>
                                                    {side ? `${side} · ` : ''}{duration}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    );
                })}
            </Stack>
        </Stack>
    );
};

export default GeneratorFleetCompact;
